/**
 * Stories — /stories route.
 *
 * Unified surface merging Curriculum (life areas) + Plan (story topics).
 * Shows life areas as expandable sections; each area displays its topics
 * with status badges, "Start session" links, inline add, and inline delete.
 *
 * Redirects from /curriculum and /plan are handled in App.tsx.
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCurriculum, type CurriculumArea } from '../lib/curriculum-api';
import { fetchStoryTopics, saveStoryTopics, type StoryTopic } from '../lib/story-topics-api';

const INPUT_CLS =
  'w-full px-4 py-3 rounded-xl border border-stone-300 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-colors';

// ── Status badge ────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: StoryTopic['status'] }) {
  const dotCls =
    status === 'captured' || status === 'complete'
      ? 'inline-block w-1.5 h-1.5 rounded-full bg-green-600 mr-1.5 align-middle'
      : 'inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5 align-middle';
  const textCls =
    status === 'captured' || status === 'complete'
      ? 'text-green-700'
      : 'text-amber-700';
  const label = status === 'complete' ? 'complete' : status === 'captured' ? 'captured' : 'pending';
  return (
    <span className={`text-xs font-medium ${textCls}`}>
      <span className={dotCls} aria-hidden="true" />
      {label}
    </span>
  );
}

// ── Stories page ────────────────────────────────────────────────────────────────

export function Stories() {
  const navigate = useNavigate();

  const [areas, setAreas] = useState<CurriculumArea[]>([]);
  const [topics, setTopics] = useState<StoryTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Expand/collapse per area
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Per-area: new topic input value
  const [addingAreaId, setAddingAreaId] = useState<string | null>(null);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const addInputRef = useRef<HTMLInputElement>(null);

  // Per-topic: delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [curriculumData, topicsData] = await Promise.all([
          fetchCurriculum(),
          fetchStoryTopics(),
        ]);
        if (!cancelled) {
          setAreas(curriculumData.areas ?? []);
          setTopics(topicsData);
        }
      } catch (err) {
        if (!cancelled) setFetchError(err instanceof Error ? err.message : 'Something went wrong. Tap to retry.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  function toggleArea(areaId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  }

  function startAddTopic(areaId: string) {
    setAddingAreaId(areaId);
    setNewTopicTitle('');
    setTimeout(() => addInputRef.current?.focus(), 0);
  }

  async function commitAddTopic(areaId: string) {
    const trimmed = newTopicTitle.trim();
    setAddingAreaId(null);
    setNewTopicTitle('');
    if (!trimmed) return;
    const newTopic: StoryTopic = {
      id: `topic-${Date.now()}`,
      areaId,
      title: trimmed,
      status: 'pending',
      order: topics.filter((t) => t.areaId === areaId).length,
      sessionIds: [],
      createdAt: new Date().toISOString(),
    };
    const snapshot = topics;
    const updated = [...topics, newTopic];
    setTopics(updated);
    try {
      await saveStoryTopics(updated);
    } catch {
      // revert on failure
      setTopics(snapshot);
    }
  }

  async function handleDeleteTopic(topicId: string) {
    setConfirmDeleteId(null);
    const snapshot = topics;
    const updated = topics.filter((t) => t.id !== topicId);
    setTopics(updated);
    try {
      await saveStoryTopics(updated);
    } catch {
      setTopics(snapshot);
    }
  }

  // ── Loading skeleton ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <main className="min-h-screen bg-stone-50 flex flex-col items-center py-12 px-4">
        <div className="w-full max-w-2xl space-y-4 animate-pulse">
          <div className="h-8 bg-stone-200 rounded w-48" />
          <div className="h-4 bg-stone-200 rounded w-72" />
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl h-14 border border-stone-200" />
          ))}
        </div>
      </main>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────────

  if (fetchError) {
    return (
      <main className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-sm text-center">
          <p className="text-red-700 text-sm">{fetchError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 rounded-lg bg-stone-800 text-white text-sm hover:bg-stone-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </main>
    );
  }

  const sortedAreas = [...areas].sort((a, b) => a.order - b.order);
  const totalTopics = topics.length;

  return (
    <main className="min-h-screen bg-stone-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-3xl font-semibold text-stone-900">My Stories</h1>
          <p className="text-sm text-stone-500 mt-1">
            Your memoir story plan — life areas and the stories you want to tell
          </p>
        </div>

        {/* Empty state */}
        {totalTopics === 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
            <p className="text-stone-700 font-medium text-lg">No stories planned yet</p>
            <p className="text-stone-500 text-sm mt-2">
              Add life areas and story topics to guide your memoir sessions.
            </p>
          </div>
        )}

        {/* Area list */}
        <div className="space-y-3">
          {sortedAreas.map((area) => {
            const areaTopics = topics.filter((t) => t.areaId === area.id);
            const isExpanded = expandedIds.has(area.id);
            const isAdding = addingAreaId === area.id;

            return (
              <div key={area.id} className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                {/* Area header row */}
                <button
                  type="button"
                  onClick={() => toggleArea(area.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-stone-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    aria-hidden="true"
                  >
                    <path fillRule="evenodd" d="M7.293 4.707a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="flex-1 text-xl font-semibold text-stone-900 truncate">{area.title}</span>
                  <span className="text-sm text-stone-500 flex-shrink-0">
                    {areaTopics.length} {areaTopics.length === 1 ? 'topic' : 'topics'}
                  </span>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-stone-100 px-5 py-3 space-y-2">
                    {/* Topic rows */}
                    {areaTopics.map((topic) => {
                      const isConfirming = confirmDeleteId === topic.id;
                      return (
                        <div key={topic.id} className="flex items-center gap-3 py-2 border-b border-stone-50 last:border-0">
                          <span className="flex-1 text-sm text-stone-800">{topic.title}</span>
                          <StatusBadge status={topic.status} />

                          {/* Start session link */}
                          <button
                            type="button"
                            onClick={() => navigate(`/session/new?topicTitle=${encodeURIComponent(topic.title)}`)}
                            className="text-xs font-medium text-amber-600 hover:underline flex-shrink-0"
                          >
                            Start session
                          </button>

                          {/* Delete */}
                          {isConfirming ? (
                            <div className="flex items-center gap-1 text-xs flex-shrink-0">
                              <span className="text-stone-600">Delete topic: Remove &apos;{topic.title}&apos;? This cannot be undone.</span>
                              <button
                                type="button"
                                onClick={() => void handleDeleteTopic(topic.id)}
                                className="px-2 py-0.5 bg-red-500 text-white rounded text-xs font-semibold"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-2 py-0.5 text-stone-500 hover:text-stone-700 text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(topic.id)}
                              className="text-stone-400 hover:text-red-500 transition-colors flex-shrink-0"
                              aria-label={`Delete topic: ${topic.title}`}
                            >
                              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* Add topic input */}
                    {isAdding ? (
                      <div className="pt-2">
                        <input
                          ref={addInputRef}
                          type="text"
                          value={newTopicTitle}
                          onChange={(e) => setNewTopicTitle(e.target.value)}
                          onBlur={() => void commitAddTopic(area.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void commitAddTopic(area.id);
                            if (e.key === 'Escape') { setAddingAreaId(null); setNewTopicTitle(''); }
                          }}
                          placeholder="Topic title…"
                          className={INPUT_CLS}
                          aria-label="New topic title"
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startAddTopic(area.id)}
                        className="mt-1 text-sm text-amber-600 hover:underline font-medium"
                      >
                        + Add a topic
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
